import React from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { formatInTimeZone } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import { getChecklistStatusLabel } from '@/pages/checklists/checklists-dashboard/checklists-dashboard.page.utils'

import { selectUserRole } from '../../../../../../../reducers/userDataReducer'
import Avatar from '../../../../../../../Avatar'
import pdfDownload from '../../../../../../../helpers/pdfDownload'
import { getChecklistTypeOptions } from '../../../../helpers'
import { getAssetLink } from '../../../../../../../helpers/get-asset-link'
import API from '../../../../../../../api/api'

const DOWNLOAD_PDF_STATUSES = ['approved', 'completed']

const ChecklistPreviewNavbar = ({
  data,
  isSubmitting,
  timezone,
  revisionMode,
  onHandleApprove,
  onRequestRevision,
  onCancelRevision,
  onSubmitRevision,
  removeChecklist,
}) => {
  const { t } = useTranslation(['checklists-dashboard', 'checklists'])
  const checklistTypeOptions = getChecklistTypeOptions(t)
  const checklistStatus = data.status
  const { avatar, firstName, lastName, email, phone, street, apartment } = data.resident
  const userRole = useSelector(selectUserRole)
  const isAdmin = userRole === 'Administrator'

  const downloadPdf = () => {
    API.downloadChecklistPDF(data.id).then((response) => {
      pdfDownload(response, `checklist-${data.id}`)
    })
  }

  return (
    <div className="checklist-preview-navbar">
      <div className="single-row space-between">
        {!revisionMode ? (
          <React.Fragment>
            <div className="checklist-type">
              {t('checklists-dashboard:previewNavbar.checklistTypeLabel', {
                type: checklistTypeOptions.find((opt) => opt.value === data.type).label,
              })}
            </div>
            <div className="action-buttons">
              <button
                type="submit"
                className={`button button-primary  ${isSubmitting ? 'is-loading' : ''}`}
                onClick={downloadPdf}
                disabled={
                  isSubmitting || !DOWNLOAD_PDF_STATUSES.includes(checklistStatus?.toLowerCase())
                }
              >
                {t('checklists-dashboard:previewNavbar.downloadPdfButton')}
              </button>
              <button
                type="submit"
                className={`button button-gray  ${isSubmitting ? 'is-loading' : ''}`}
                onClick={removeChecklist}
                disabled={(isSubmitting || checklistStatus === 'completed') && !isAdmin}
              >
                {t('checklists-dashboard:previewNavbar.removeChecklistButton')}
              </button>
              <button
                type="submit"
                className={`button button-black  ${isSubmitting ? 'is-loading' : ''}`}
                onClick={onRequestRevision}
                disabled={isSubmitting || checklistStatus !== 'pending-review'}
              >
                {t('checklists-dashboard:previewNavbar.requestRevisionButton')}
              </button>
              <button
                type="submit"
                className={`button button-primary ${isSubmitting ? 'is-loading' : ''}`}
                onClick={onHandleApprove}
                disabled={isSubmitting || checklistStatus !== 'pending-review'}
              >
                {t('checklists-dashboard:previewNavbar.approveButton')}
              </button>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className="checklist-revision-mode">
              {t('checklists-dashboard:previewNavbar.revisionModeLabel')}
            </div>
            <div className="action-buttons revision-mode">
              <button
                type="submit"
                className={`button button-black is-block ${isSubmitting ? 'is-loading' : ''}`}
                onClick={onCancelRevision}
                disabled={isSubmitting}
              >
                {t('checklists-dashboard:previewNavbar.cancelRevisionButton')}
              </button>
              <button
                type="submit"
                className={`button button-primary is-block ${isSubmitting ? 'is-loading' : ''}`}
                onClick={onSubmitRevision}
                disabled={isSubmitting}
              >
                {t('checklists-dashboard:previewNavbar.submitRevisionsButton')}
              </button>
            </div>
          </React.Fragment>
        )}
      </div>
      <div className="single-row">
        <div className="single-block">
          <div className="block-title">{t('checklists-dashboard:shared.residentLabel')}</div>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Avatar
              size="big"
              style={{ marginRight: '10px' }}
              square
              zoomTitle={`${firstName} ${lastName}`}
              zoomable
            >
              <img
                src={getAssetLink({
                  path: 'chitown-avatar/avatar',
                  file: avatar || 'avatar.png',
                })}
              />
            </Avatar>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="block-contain name">
                {firstName} {lastName}
              </div>
              <div className="block-contain">{email}</div>
            </div>
          </div>
        </div>
        <div className="single-block">
          <div className="block-title">{t('checklists-dashboard:shared.phoneNumberLabel')}</div>
          <div className="block-contain">{phone}</div>
        </div>
        <div className="single-block">
          <div className="block-title">{t('checklists-dashboard:shared.addressLabel')}</div>
          <div className="block-contain mini-bold">{`#${apartment}`}</div>
          <div className="block-contain">{street}</div>
        </div>
        <div className="single-block">
          <div className="block-title">
            {t('checklists-dashboard:previewNavbar.createdAtLabel')}
          </div>
          <div className="block-contain">
            {formatInTimeZone(data.createdAt, timezone, 'MM/dd/yyyy')}
          </div>
        </div>
        <div className="single-block">
          <div className="block-title">{t('checklists-dashboard:previewNavbar.dueDateLabel')}</div>
          <div className="block-contain">
            {formatInTimeZone(data.dueDate, timezone, 'MM/dd/yyyy')}
          </div>
        </div>
        <div className="single-block">
          <div className="block-title">{t('checklists-dashboard:previewNavbar.statusLabel')}</div>
          <div className={`block-status-filter ${checklistStatus.replace(/\s+/g, '')}`}>
            {getChecklistStatusLabel(checklistStatus, t)}
          </div>
        </div>
      </div>
    </div>
  )
}

ChecklistPreviewNavbar.propTypes = {
  data: PropTypes.object,
  isSubmitting: PropTypes.bool,
  timezone: PropTypes.string,
  revisionMode: PropTypes.bool,
  onHandleApprove: PropTypes.func,
  onRequestRevision: PropTypes.func,
  onCancelRevision: PropTypes.func,
  onSubmitRevision: PropTypes.func,
  removeChecklist: PropTypes.func,
}

export default ChecklistPreviewNavbar
